// These transforms need to be adapted to Iceland
import {useLocation} from 'react-router-dom'
import he from "he"
import { capitalize } from '../utils/utils'
import { CONTENT_SCHEMAS } from '../components/cms/utils'

const transformCategories = (productCategories) => {
  let categoryProps = {}
  if (productCategories) {
    let categoryCount = 0
    Object.keys(productCategories).forEach(key => {
      let category = productCategories[key]
      let categoryIndex = parseInt(key)
      if (!isNaN(categoryIndex) && categoryIndex >= 0 && categoryIndex < 3) {
        categoryCount++
        let categoryParts = category.split(' > ')
        let categoryName = categoryParts[categoryParts.length - 1]
        categoryName = he.decode(categoryName)
        categoryProps[`itemCategory${categoryCount === 1 ? '' : categoryCount}`] = categoryName
      }
    })
  }
  return categoryProps
}

export const transformAlgoliaItemsForGA4 = (items, hitItemIndex, itemListName) => {
  return items.map((item, index) => {    
    const productCategories = item?.__primary_category
    const categoryProps = transformCategories(productCategories)

    let wasPrice = 0
    let currentPrice = item?.price?.GBP ?? 0

    if(item && Object.prototype.hasOwnProperty.call(item, 'priceInfo') && Object.prototype.hasOwnProperty.call(item.priceInfo, 'wasPrice')) {
      wasPrice = parseFloat(item.priceInfo.wasPrice)
    }
    
    const discount = wasPrice > 0 && currentPrice > 0 ? Number((wasPrice - currentPrice).toFixed(2)) : 0

    return {
      item_id: item?.id ?? '',
      item_name: item?.name ?? '',
      item_category: categoryProps.itemCategory ?? '',
      item_category2: categoryProps.itemCategory2 ?? '',
      item_category3: categoryProps.itemCategory3 ?? '',
      item_brand: item?.brand ?? '',
      index: hitItemIndex ? hitItemIndex + 1 : index + 1,
      quantity: 1,
      price: currentPrice,
      discount: discount,
      in_stock: item?.in_stock || item?.inStock,
      item_list_name: itemListName ?? '',
      currency: 'GBP'
    }
  })
}

export const transformAlgoliaItemsForBasketEventsGA4 = (items, cartLocation, viewingCategoryId, searchQuery, hitItemIndex) => {
  return items.map((item, index) => {
    const productCategories = item?.__primary_category
    const categoryProps = transformCategories(productCategories)

    let wasPrice = 0
    let currentPrice = item?.price?.GBP ?? 0

    if (item && Object.prototype.hasOwnProperty.call(item, 'priceInfo') && Object.prototype.hasOwnProperty.call(item.priceInfo, 'wasPrice')) {
      wasPrice = parseFloat(item.priceInfo.wasPrice)
    }

    const discount = wasPrice > 0 && currentPrice > 0 ? Number((wasPrice - currentPrice).toFixed(2)) : 0

    let categoryName = ''

    for (const categoryArray of item.categories) {
      for (const category of categoryArray) {
        if (category.id === viewingCategoryId) {
          categoryName = he.decode(category.name)
        }
      }
    }

    let itemListName, itemListId = ''

    if (cartLocation === (CART_LOCATION.SEARCH || CART_LOCATION.AUTOCOMPLETE)) {
      itemListId = searchQuery
      itemListName = cartLocation
    } else if (cartLocation === CART_LOCATION.PDP) {
      itemListId = ''
      itemListName = ''
    } else if (cartLocation !== CART_LOCATION.PLP) {
      itemListId = ''
      itemListName = cartLocation
    }  else {
      itemListId = viewingCategoryId
      itemListName = categoryName
    }

    return {
      item_id: item?.id ?? '',
      item_name: item?.name ?? '',
      item_category: categoryProps.itemCategory ?? '',
      item_category2: categoryProps.itemCategory2 ?? '',
      item_category3: categoryProps.itemCategory3 ?? '',
      item_brand: item?.brand ?? '',
      index: hitItemIndex ? hitItemIndex + 1 : index + 1,
      quantity: 1,
      price: currentPrice,
      discount: discount,
      in_stock: item?.in_stock || item?.inStock,
      item_list_id: itemListId ?? '',
      item_list_name: itemListName ?? ''
    }
  })
}

export const transformSalesforceItemsForGA4 = (items) => {
  return items.map((item, idx) => {
    return {
      item_name: item.name,
      item_id: item.id,
      price: item?.price, // Needs to change when locales are merged
      item_brand: item.c_brandCategory,
      item_category: item.primaryCategoryId,
      item_category_2: '',
      item_category_3: '',
      item_category_4: '',
      item_variant: item?.values?.color,
      item_list_name: '',
      item_list_id: item.primary_category_id,
      index: idx,
      size: item?.values?.size,
      quantity: 1,
    }
  })
}

export const transformSalesforceBasketItemsForGA4 = (items, productsById) => {
  return items.map((productItem) => {
    const product = productItem.productId ? productsById[productItem.productId] : undefined
    const productCategories = product?.__primary_category
    const categoryProps = transformCategories(productCategories)
    const price = productItem.basePrice

    let wasPrice = 0
    if(product && Object.prototype.hasOwnProperty.call(product, 'priceInfo') && Object.prototype.hasOwnProperty.call(product.priceInfo, 'wasPrice')) {
      wasPrice = parseFloat(product.priceInfo.wasPrice)
    }

    return {
      item_id: productItem.productId,
      item_name: productItem.productName,
      item_category: categoryProps.itemCategory ?? '',
      item_category2: categoryProps.itemCategory2 ?? '',
      item_category3: categoryProps.itemCategory3 ?? '',
      item_brand: product.brand ?? '',
      quantity: productItem.quantity ?? 0,
      price: price,
      discount: wasPrice > 0 ? Number((wasPrice - price).toFixed(2)) : 0
    }
  })
}

export const transformNavMenuPromoItemsForGA4 = (promotions, navMenuLevel, hasNavFolders) => {
  if (!promotions) return

  return promotions
    .flatMap((childItem) => {
      let creativeName = 'No category ID'
      const categoryId = childItem.categoryId || (hasNavFolders ? childItem.children?.[0]?.categoryId : 'No category ID')

      if (categoryId !== undefined && categoryId !== 'undefined') {
        creativeName = categoryId
      }
        
      return {
        creative_name: creativeName,
        creative_slot: navMenuLevel,
        promotion_id: childItem.path ?? 'Nav folder',
        promotion_name: childItem.name,
      }
    })
    .filter(Boolean)
}

export const transformNavMenuPromoBannersForGA4 = (flyOutContent) => {
  const promoBanners = flyOutContent.default.find(
    (item) => item._meta && item._meta.schema === CONTENT_SCHEMAS.NAV_FLYOUT_BANNER,
  )

  return promoBanners.listItems?.map(({image, imageLink}, index) => {
    return {
      creative_name: image?.images?.[0]?.data?.altText ?? 'Banner missing alt text',
      creative_slot: `${promoBanners._meta?.name} ${index + 1}` ?? 'Navigation flyout banner',
      promotion_id: imageLink ?? 'Banner without link',
      promotion_name: 'No CTA',
    }
  })
}

export const CART_LOCATION = {
  AUTOCOMPLETE: 'Quick Search',
  CHECKOUT: 'Checkout',
  PLP: 'PLP',
  PDP: 'PDP',
  BASKET: 'Basket',
  MINIBASKET: 'Mini Basket',
  SEARCH: 'Search',
  PROMO: 'Promotional Modal',
  CAROUSEL: "Product Carousel"
}

export const useSlotLocation = () => {
  const {pathname} = useLocation()
  let slotLocation

  if (pathname === '/') {
    slotLocation = 'homepage'
  } else {
    slotLocation = pathname.split('/').filter(part => part)
    slotLocation = slotLocation[slotLocation.length - 1].replace(/-/g, ' ')
  }
  return slotLocation
}

/**
 * Takes the schema string and returns the component name (e.g. thin-banner)
 * @param {string} schema from Amplience
 * @returns the component name as a string
 */
export const getComponentName = (schema) => {
  if (!schema) return null
  const componentName = schema
    .split('/')
    .pop()
    .replace('.json', '')
  return componentName
}

/**
 * Creates the promotion object for the select promotion event
 * @param {Object} content the content object from Amplience
 * @param {string} slotGroupName the slot name from Amplience
 * @param {number} index the index of the content within the slot
 * @returns {Object | null} promotion: an object with data required for the dataLayer
 */
export const createPromoForSelectPromotion = (props) => {
  const {content, slotGroupName, slotPosition, campaign = [], index = 1} = props
  if (!content) return null
  const schema = content?._meta?.schema
  // Create a component name string for the event
  const component = schema ? getComponentName(schema) : 'component'
  // Create a capitalized component name string for the event
  const componentCapitalized = capitalize(component).replace('-', ' ')
  const promotion = {
    creative_name: content._meta.name ?? `${componentCapitalized}${index > 1 ? ` #${index}` : ''} missing asset title`,
    creative_slot: component && slotGroupName ? `${component}_${slotGroupName}` : `${componentCapitalized}${index > 1 ? ` #${index}` : ''}`,
    promotion_id: content.cta?.ctaLink || content.imageLink || `${componentCapitalized}${index > 1 ? ` #${index}` : ''} without link`,
    promotion_name: content.cta?.ctaText || `${componentCapitalized}${index > 1 ? ` #${index}` : ''} without cta text`,
    link_position: index ?? 1,
    campaign_ID: content.campaign?.[0]?.campaignId || campaign?.[0]?.campaignId,
    slot_position: slotPosition
  }
  return promotion
}

/**
 * Creates a promotion object for a single item
 * @param {Object} item The individual promotion item
 * @param {Object} promotion The parent promotion object
 * @param {number} index The index of the promotion
 * @param {number} itemIndex The index of the item within the promotion
 * @returns {Object|null} A promotion object for the dataLayer or null if item is invalid
 */
const createPromotionItemObject = (item, promotion, index, itemIndex) => {
  if (!item) return null

  const schema = item?._meta?.schema
  const slotGroupName = item?.slotGroupName || promotion._meta?.name
  const slotPosition = item?.slotIndex || promotion?.slotIndex

  // Create a component name string for the event
  const component = schema ? getComponentName(schema) : 'component' 
  // Create a capitalized component name string for the event
  const componentCapitalized = capitalize(component).replaceAll('-', ' ')
  
  // Build the promotion object
  return {
    creative_name: item._meta?.name ?? `${componentCapitalized}${itemIndex > 0 ? ` #${itemIndex + 1}` : ''} missing asset title`,
    creative_slot: component && slotGroupName ? `${component}_${slotGroupName}` : `${componentCapitalized}${index > 0 ? ` #${itemIndex + 1}` : ''}`,
    promotion_id: item.cta?.ctaLink || item.imageLink || `${componentCapitalized}${itemIndex > 0 ? ` #${itemIndex + 1}` : ''} without link`,
    promotion_name: item.cta?.ctaText || `${componentCapitalized}${itemIndex > 0 ? ` #${itemIndex + 1}` : ''} without cta text`,
    slot_position: slotPosition,
    campaign_ID: item.campaign?.[0]?.campaignId || promotion.campaign?.[0]?.campaignId
  }
}

/**
 * Processes a single promotion and returns an array of promotion items
 * @param {Object} promotion The promotion object
 * @param {number} index The index of the promotion within the slot group
 * @returns {Object[]} An array of promotion items
 */
const processPromotionItems = (promotion, index) => {
  // Get individual content items from the promotion object
  return getPromotionItems(promotion).flatMap((item, itemIndex) => 
    createPromotionItemObject(item, promotion, index, itemIndex)
  )
}

/**
 * Processes a slot group and returns an array of promotions
 * @param {Object[]} slotGroup An array of promotions in a slot group
 * @returns {Object[]} An array of processed promotions
 */
const processSlotGroup = (slotGroup) => {
  // Create the promotion object for each entry in the slotGroup array
  return slotGroup.flatMap((promotion, index) => processPromotionItems(promotion, index))
}

/**
 * Creates the promotion objects for the view promotion event
 * @param {Object[]} content Array containing content objects from Amplience
 * @returns {Object[]} of promotion objects with data required for the dataLayer
 */
export const createPromoForViewPromotion = (content) => {
  if (!content || !Array.isArray(content)) return []
  
  const promotions = content.flatMap(processSlotGroup).filter(Boolean)
  return promotions
}

/** Gets individual content items within the promotion object
 * i.e. Carousels, Category Tiles, Offer Cards
 * @param {Object} promotion the promotion object
 * @returns {Object[]} an array of items from the promotion
 */
const getPromotionItems = (promotion) => {
  // Check for different array properties in the promotion object
  if (Array.isArray(promotion.tileList)) return promotion.tileList
  if (Array.isArray(promotion.items)) return promotion.items
  if (Array.isArray(promotion.cardList)) return promotion.cardList
  
  // If no array properties found, treat the promotion itself as a single item
  return [promotion]
}